import { makeEsArticle, type EsArticleInput } from "./learnFactoryEs";
import type { LearnFAQ } from "@/pages/learn/learnShared";

/**
 * /es/learn/ampa-tms-reviews-what-to-expect
 */

const AMPA_TRADEMARK =
  "Ampa y Ampa One son marcas registradas de Ampa Inc. Neuro Wellness TMS Centers of America es un proveedor independiente y no pertenece a Ampa.";

const faqs: LearnFAQ[] = [
  {
    question: "¿Duele el TMS Ampa?",
    answer:
      "La mayoría de los pacientes sienten una sensación de golpecitos en el cuero cabelludo durante la estimulación, no dolor. El efecto secundario más común es una molestia leve en el cuero cabelludo que suele desaparecer a medida que avanza la sesión. Las sesiones de Ampa One son cortas, unos minutos de estimulación, lo que puede hacerlas más cómodas que las sesiones convencionales más largas. No se usa anestesia y puede conducir a casa después.",
  },
  {
    question: "¿Cuáles son los efectos secundarios de Ampa One?",
    answer:
      "Los efectos secundarios más comunes son molestias leves en el cuero cabelludo y dolor de cabeza que suelen desaparecer durante o después de la sesión. Con menos frecuencia, los pacientes reportan mareo. Como con todo TMS, existe un riesgo raro de convulsión. El TMS no es adecuado para personas con ciertos implantes metálicos o electrónicos en o cerca de la cabeza, marcapasos/DAI, o un trastorno convulsivo. Una evaluación médica decide si es seguro para usted.",
  },
  {
    question: "¿Cuándo empezaré a sentirme mejor?",
    answer:
      "La respuesta varía. Algunos pacientes notan un cambio en la primera semana; en otros se acumula a lo largo del tratamiento. Medimos la respuesta con la escala de depresión PHQ-9 antes, durante y después del tratamiento para que el progreso se siga, no se adivine. Ampa ha reportado resultados tempranos no aleatorizados de 87 % de respuesta y 72 % de remisión a las 6 semanas (comunicado de NRx Pharmaceuticals, noviembre de 2025); son resultados tempranos y los resultados individuales varían.",
  },
  {
    question: "¿Es seguro el TMS Ampa?",
    answer:
      "Ampa One está autorizado por la FDA para el trastorno depresivo mayor en adultos. El TMS no es invasivo: sin cirugía, sin anestesia. Los efectos secundarios más comunes son leves y temporales. Los riesgos graves son raros e incluyen convulsión. La seguridad se evalúa antes del tratamiento: ciertos implantes, marcapasos/DAI, metal en la cabeza o los ojos, o un trastorno convulsivo pueden hacer que el TMS no sea seguro para usted. Una evaluación médica toma la decisión final.",
  },
  {
    question: "¿Qué dicen los pacientes de Ampa One?",
    answer:
      "Los pacientes describen las sesiones de Ampa One como rápidas, con una sensación de golpecitos en el cuero cabelludo y sin anestesia. Las historias compartidas por Ampa, el fabricante del dispositivo (en ampahealth.com/patients), no son pacientes de Neuro Wellness y los resultados individuales varían. A medida que recopilamos nuestras propias reseñas de Google que mencionan Ampa One, las añadimos aquí con el permiso del paciente.",
  },
];

export const esAmpaTmsReviewsWhatToExpect = makeEsArticle({
  slug: "ampa-tms-reviews-what-to-expect",
  title:
    "Reseñas del TMS Ampa One y qué esperar: sesiones, efectos secundarios, resultados",
  description:
    "Cómo es realmente el TMS Ampa One: sesiones, efectos secundarios, cronología de resultados, historias de pacientes del fabricante y qué esperar en nuestra clínica de Coral Springs.",
  h1: "TMS Ampa One: reseñas, efectos secundarios y cómo es realmente el tratamiento",
  breadcrumbLabel: "Reseñas de Ampa One y qué esperar",
  shortAnswer:
    "Los pacientes describen las sesiones de Ampa One como rápidas (unos minutos de estimulación), con una sensación de golpecitos en el cuero cabelludo y sin anestesia. Los efectos secundarios más comunes son molestias leves en el cuero cabelludo y dolor de cabeza que suelen desaparecer. A continuación, historias de pacientes publicadas por el fabricante, además de qué esperar en nuestra clínica de Coral Springs. Los resultados individuales varían. No se garantiza ningún resultado. La cobertura varía según el plan. Ampa One está disponible con receta tras una evaluación clínica.",
  keyTakeaways: [
    "Las sesiones son cortas: unos minutos de estimulación con una sensación de golpecitos, sin anestesia.",
    "Efectos secundarios más comunes: molestias leves en el cuero cabelludo y dolor de cabeza que suelen desaparecer.",
    "La respuesta se sigue con el PHQ-9 antes, durante y después del tratamiento.",
    "Ampa ha reportado resultados tempranos no aleatorizados: 87 % de respuesta, 72 % de remisión a las 6 semanas (comunicado de NRx, nov. 2025). Los resultados individuales varían.",
    "Las historias de pacientes compartidas por Ampa (el fabricante) no son pacientes de Neuro Wellness.",
    "Puede conducir a casa y volver al trabajo después de una sesión.",
  ],
  sections: [
    {
      heading: "Respuesta rápida",
      paragraphs: [
        "Las sesiones de Ampa One son rápidas: aproximadamente un minuto de preparación y unos minutos de estimulación, con un sonido y una sensación de golpecitos en el cuero cabelludo y sin anestesia. Los efectos secundarios más comunes son molestias leves en el cuero cabelludo y dolor de cabeza que suelen desaparecer. A continuación, historias de pacientes publicadas por el fabricante del dispositivo, además de qué esperar en nuestra clínica de Coral Springs.",
      ],
    },
    {
      heading: "Antes de su primera sesión",
      paragraphs: [
        "Antes de su primera sesión, tendrá un tamizaje para confirmar que Ampa One es seguro y adecuado para usted. Vista algo cómodo, coma con normalidad y lleve auriculares y un portátil o un libro si quiere algo para los descansos. Retire joyas y accesorios metálicos de la cabeza y el cuello antes del tratamiento.",
      ],
    },
    {
      heading: "Día uno: ajuste del gorro, mapeo, primera sesión",
      paragraphs: [
        "El día uno, el técnico ajusta el gorro de neuronavegación preimpreso a su cabeza y luego realiza el mapeo del umbral motor para fijar su nivel de estimulación. La cámara integrada en la bobina sigue el gorro para que cada sesión permanezca en el mismo objetivo. Su primera sesión son unos minutos de estimulación con una sensación de golpecitos. Puede conducir a casa y volver al trabajo después.",
      ],
    },
    {
      heading: "Efectos secundarios y seguridad",
      paragraphs: [
        "Los efectos secundarios más comunes son molestias leves en el cuero cabelludo y dolor de cabeza que suelen desaparecer durante o después de la sesión. Con menos frecuencia, los pacientes reportan mareo. Como con todo TMS, existe un riesgo raro de convulsión. El TMS no es adecuado para personas con ciertos implantes metálicos o electrónicos en o cerca de la cabeza (clips de aneurisma, implantes cocleares, estimuladores implantados), marcapasos/DAI, fragmentos de metal en la cabeza o los ojos, o un trastorno convulsivo. Una evaluación médica toma la decisión final.",
      ],
    },
    {
      heading: "Historias de pacientes publicadas por Ampa",
      paragraphs: [
        "Las historias compartidas por Ampa, el fabricante del dispositivo, se publican en ampahealth.com/patients. Son historias de Ampa, no de pacientes de Neuro Wellness, y los resultados individuales varían. A medida que recopilamos nuestras propias reseñas de Google que mencionan Ampa One, las añadimos aquí con el permiso del paciente. No usamos esquema Review o AggregateRating para historias de terceros.",
      ],
    },
    {
      heading: "Cómo se miden los resultados",
      paragraphs: [
        "Medimos la respuesta con la escala de depresión PHQ-9 antes, durante y después del tratamiento, para que el progreso se siga, no se adivine. Algunos pacientes notan un cambio en la primera semana; en otros se acumula a lo largo del tratamiento. Ampa ha reportado resultados tempranos no aleatorizados de 87 % de respuesta y 72 % de remisión a las 6 semanas (comunicado de NRx Pharmaceuticals, 10 de noviembre de 2025); son resultados tempranos no aleatorizados y los resultados individuales varían.",
      ],
    },
  ],
  faqs,
  sources: [
    {
      citation:
        "Ampa Inc. Historias de pacientes. ampahealth.com/patients (consultado el 3 de noviembre de 2026).",
      url: "https://www.ampahealth.com/patients",
    },
    {
      citation:
        "NRx Pharmaceuticals. Comunicado de prensa sobre resultados tempranos de Ampa One (10 de noviembre de 2025).",
    },
  ],
  relatedLinks: [
    {
      title: "TMS Ampa One™ en Florida",
      href: "/es/ampa",
      description: "Tratamiento de depresión acelerado y guiado por cámara",
    },
    {
      title: "TMS de un día en Florida",
      href: "/es/one-day-tms",
      description: "El protocolo Ampa One Day explicado",
    },
    {
      title: "Costo del TMS Ampa One",
      href: "/es/learn/ampa-tms-cost",
      description: "Precio, seguro y opciones de pago",
    },
    {
      title: "SAINT® vs Ampa One",
      href: "/es/learn/saint-vs-ampa-one",
      description: "Dos opciones de TMS acelerado, una clínica en Florida",
    },
    {
      title: "Solicitar una consulta",
      href: "/es/contactus",
      description: "Reserve una consulta gratuita con nuestro equipo clínico",
    },
  ],
  reviewBlock: {
    writtenBy: "Neuro Wellness TMS Centers of America",
    reviewedBy: "Daniel Pietras, MD",
    reviewedByHref: "/dr-daniel-pietras-md",
    published: "3 de noviembre de 2026",
    lastReviewed: "3 de noviembre de 2026",
    lastUpdated: "3 de noviembre de 2026",
  },
  toc: [
    { label: "Respuesta rápida", href: "#respuesta-rapida" },
    {
      label: "Antes de su primera sesión",
      href: "#antes-de-su-primera-sesion",
    },
    {
      label: "Día uno",
      href: "#dia-uno-ajuste-del-gorro-mapeo-primera-sesion",
    },
    {
      label: "Efectos secundarios y seguridad",
      href: "#efectos-secundarios-y-seguridad",
    },
    {
      label: "Historias de pacientes",
      href: "#historias-de-pacientes-publicadas-por-ampa",
    },
    {
      label: "Cómo se miden los resultados",
      href: "#como-se-miden-los-resultados",
    },
    { label: "Preguntas frecuentes", href: "#faq" },
    { label: "Investigación citada", href: "#cited-research" },
  ],
  safetyBlock:
    "Los resultados individuales varían. No se garantiza ningún resultado. La cobertura varía según el plan. Ampa One está disponible con receta tras una evaluación clínica. Los efectos secundarios comunes incluyen molestias leves en el cuero cabelludo y dolor de cabeza que suelen desaparecer. Si está en crisis, llame o envíe un mensaje de texto al 988.",
  trademarkFooter: AMPA_TRADEMARK,
  ctaBand: {
    headline: "Vea si Ampa One es adecuado para usted, sin costo",
    primaryText: "Solicitar una consulta gratuita",
    primaryHref: "/es/ampa",
    secondaryText: "Llamar 954-827-9793",
    secondaryHref: "tel:9548279793",
    phone: "954-827-9793",
  },
} as EsArticleInput);
